import { useCallback, useEffect, useMemo, useState } from 'react';
import LinkableTextArea from './LinkableTextArea.jsx';
import RecordDetailPopup from './RecordDetailPopup.jsx';
import DeliverableSetEditor from './DeliverableSetEditor.jsx';
import {
  getActors,
  getCategories,
  getDeliverableManagements,
  getEmployees,
  getMiddleCategories,
  getOems,
  getOemRegions,
  getPhases,
  getProblems,
  getProgressSchedules,
  getRegions,
  getRisks,
  getSmallCategories,
  getStatuses,
  deleteProgressSchedule,
  updateProgressSchedule,
} from '../api/dashboardApi.js';
import { canEdit } from '../../../auth/permissions.js';
import {
  buildOptionsByOemRegion,
  validateDeliverables,
  buildDeliverableOptionsByCoordinate,
  getDeliverableOptionsForSchedule,
} from '../utils/converters.js';
import { buildConflictMessage } from '../utils/conflictMessage.js';
import '../styles/input-controls.css';

/**
 * 進捗入力ページ (DashboardProgressManagementPage) と同じカラム定義。
 * EditableRecordTable 経由で使われるものと完全に一致させる。
 */
const BASE_COLUMNS = [
  { key: 'oem_region', label: 'OEM×対地', type: 'select', width: '180px', required: true },
  { key: 'deadline_check', label: '期限チェック', type: 'text', readOnly: true },
  { key: 'inconsistency_check', label: '不整合チェック', type: 'text', readOnly: true },
  { key: 'category_name', label: 'カテゴリ', type: 'select', required: true },
  { key: 'large_category_name', label: '大項目', type: 'select', required: true },
  { key: 'middle_category_name', label: '中項目', type: 'select', required: true },
  { key: 'small_category', label: '小項目', type: 'select', required: true },
  { key: 'phase_name', label: 'Phase', type: 'select' },
  { key: 'milestone', label: 'マイルストーン', type: 'checkbox' },
  // 成果物は1成果物=3行（名前・URL・チェック2つ）のまとまりで編集する
  { key: 'deliverables', label: '成果物', type: 'custom' },
  { key: 'actor_name', label: '領域', type: 'select', required: true },
  { key: 'assignees_display', label: '担当者', type: 'multi_items' },
  { key: 'status_name', label: 'ステータス', type: 'select' },
  { key: 'start_date_plan', label: '開始日（予定）', type: 'date' },
  { key: 'end_date_plan', label: '終了日（予定）', type: 'date' },
  { key: 'start_date_achievement', label: '開始日（実績）', type: 'date' },
  { key: 'end_date_achievement', label: '終了日（実績）', type: 'date' },
  { key: 'successor_task', label: '後続タスク', type: 'multi_items' },
  { key: 'related_risk', label: '関連リスク', type: 'multi_items' },
  { key: 'related_problem', label: '関連課題', type: 'multi_items' },
  { key: 'memo', label: '備考', type: 'text' },
  { key: 'created_by', label: '作成者', type: 'text', readOnly: true },
  { key: 'created_at', label: '作成日時', type: 'text', readOnly: true },
  { key: 'updated_by', label: '最終更新者', type: 'text', readOnly: true },
  { key: 'updated_at', label: '最終更新日時', type: 'text', readOnly: true },
];

/* ---------- ヘルパー ---------- */

function buildLookupMap(items, key, idKey = 'id') {
  const map = new Map();
  items.forEach((item) => {
    if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
      map.set(item[key], item[idKey]);
    }
  });
  return map;
}

function buildMiddleCategoryLookup(items) {
  const pairMap = new Map();
  const byNameMap = new Map();
  items.forEach((item) => {
    const middleName = item.middle_category;
    const largeName = item.large_category?.name;
    if (middleName) {
      if (!byNameMap.has(middleName)) byNameMap.set(middleName, item.id);
      if (largeName) pairMap.set(`${largeName}::${middleName}`, item.id);
    }
  });
  return { pairMap, byNameMap };
}

function buildSmallCategoryLookup(items) {
  const pairMap = new Map();
  const byNameMap = new Map();
  items.forEach((item) => {
    const smallName = item.small_category;
    const middleName = item.middle_category?.name;
    if (smallName) {
      if (!byNameMap.has(smallName)) byNameMap.set(smallName, item.id);
      if (middleName) pairMap.set(`${middleName}::${smallName}`, item.id);
    }
  });
  return { pairMap, byNameMap };
}

function resolveEmployeeIds(displayValue, employeeByName) {
  /* istanbul ignore if -- 担当者もマスタも保存時点では必ず揃っている */
  if (!displayValue || !employeeByName) return [];
  return displayValue
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => employeeByName.get(name))
    .filter(Boolean);
}

/**
 * PATCH（部分更新）用ペイロード。
 * 解決できたIDフィールドのみ含め、常にオブジェクトを返す。
 * optional chaining で refs が不完全でもクラッシュしない。
 */
function toPatchPayload(row, refs) {
  const payload = {};

  if (row.oem_region && refs.oemRegionLookup?.has(row.oem_region)) {
    const resolved = refs.oemRegionLookup.get(row.oem_region);
    payload.oem_id = resolved.oemId;
    payload.region_id = resolved.regionId;
  }

  const categoryId = refs.categoryByName?.get(row.category_name);
  if (categoryId) payload.category_id = categoryId;

  const actorId = refs.actorByName?.get(row.actor_name);
  if (actorId) payload.actor_id = actorId;

  const statusId = refs.statusByName?.get(row.status_name);
  if (statusId) payload.status_id = statusId;

  /* istanbul ignore else -- 中項目は必須項目なので保存時点で必ず入っている */
  if (row.middle_category_name) {
    /* istanbul ignore next -- 大項目も必須なので空文字にはならない */
    const middleKey = `${row.large_category_name || ''}::${row.middle_category_name}`;
    const middleCategoryId = refs.middleByPair?.get(middleKey) || refs.middleByName?.get(row.middle_category_name);
    /* istanbul ignore else -- 選択式なのでマスタから必ず解決できる */
    if (middleCategoryId) payload.middle_category_id = middleCategoryId;
  }

  /* istanbul ignore else -- 小項目は必須項目なので保存時点で必ず入っている */
  if (row.small_category) {
    /* istanbul ignore next -- 中項目も必須なので空文字にはならない */
    const smallKey = `${row.middle_category_name || ''}::${row.small_category}`;
    const smallCategoryId = refs.smallByPair?.get(smallKey) || refs.smallByName?.get(row.small_category);
    /* istanbul ignore else -- 選択式なのでマスタから必ず解決できる */
    if (smallCategoryId) payload.small_category_id = smallCategoryId;
  }

  // Phase は未選択に戻せる必要があるため null でも送る。
  // マスタ未ロード時は名前を解決できず既存の Phase を誤って消すため、その場合だけ送らない。
  /* istanbul ignore else -- マスタ未ロード中は保存ボタンが無効なので refs は必ず揃っている */
  if (refs.phaseByName) {
    payload.phase_id = refs.phaseByName.get(row.phase_name) || null;
  }

  payload.milestone = Boolean(row.milestone);
  // 成果物は成果物1件ごとの1セット（名前・URL・保管済み・PMO成果物保管チェック）
  /* istanbul ignore next -- 成果物は常に配列として保持される */
  payload.deliverables = (Array.isArray(row.deliverables) ? row.deliverables : []).map((d) => ({
    id: d.id ?? null,
    
    product: String(d.product || '').trim(),
    
    deliverable_url: d.deliverable_url || '',
    no_deliverable: Boolean(d.no_deliverable),
    is_deliverable_stored: Boolean(d.is_deliverable_stored),
    pmo_deliverable_check: Boolean(d.pmo_deliverable_check),
  }));
  // 成果物リンクの競合検出用（中間テーブルのバージョン）
  payload.link_version = row.link_version ?? null;
  payload.start_date_plan = row.start_date_plan || null;
  payload.end_date_plan = row.end_date_plan || null;
  payload.start_date_achievement = row.start_date_achievement || null;
  payload.end_date_achievement = row.end_date_achievement || null;
  payload.memo = row.memo || '';

  // employee_ids は解決できた場合のみ送信（空配列だと担当者がクリアされる）
  const employeeIds = resolveEmployeeIds(row.assignees_display, refs.employeeByName);
  if (employeeIds.length > 0) {
    payload.employee_ids = employeeIds;
  }

  // successor_task_ids: 後続タスク名 → ProgressSchedule ID に変換
  /* istanbul ignore else -- 同上。refs は必ず揃っている */
  if (refs.scheduleByOemSmall) {
    const names = String(row.successor_task || '').split(',').map((s) => s.trim()).filter(Boolean);
    /* istanbul ignore next -- OEM×対地は必須項目なので空にならない */
    const oemRegion = row.oem_region || '';
    payload.successor_task_ids = names
      .map((name) => refs.scheduleByOemSmall.get(`${oemRegion}::${name}`))
      .filter(Boolean);
  }

  // risk_ids / problem_ids: 関連リスク・関連課題名 → ID に変換
  // バックエンドは配列受信時に全置換するため、ルックアップがある場合のみ送信する
  /* istanbul ignore else -- 同上 */
  if (refs.riskByTitle) {
    /* istanbul ignore next -- 未入力時は空文字が入るため右辺には落ちない */
    payload.risk_ids = String(row.related_risk || '').split(',').map((s) => s.trim()).filter(Boolean)
      .map((name) => refs.riskByTitle.get(name)).filter(Boolean);
  }
  /* istanbul ignore else -- 同上 */
  if (refs.problemByTitle) {
    payload.problem_ids = String(row.related_problem || '').split(',').map((s) => s.trim()).filter(Boolean)
      .map((name) => refs.problemByTitle.get(name)).filter(Boolean);
  }

  // 楽観ロック用タイムスタンプ（ISO）を送信
  if (row.updated_at_iso) {
    payload.updated_at_iso = row.updated_at_iso;
  }

  return payload;
}

function buildSelectOptionsFromRows(rows, keys) {
  const optionsByKey = Object.fromEntries(keys.map((key) => [key, new Set()]));
  rows.forEach((row) => {
    keys.forEach((key) => {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        optionsByKey[key].add(value);
      }
    });
  });
  return Object.fromEntries(
    Object.entries(optionsByKey).map(([key, values]) => [key, ['', ...Array.from(values)]]),
  );
}


/**
 * selectオプション配列に現在のrow値を確実に含める。
 * マスタからビルドしたオプションにrowの値が含まれない場合がある。
 */
function ensureValueInOptions(opts, value) {
  if (!value || opts.includes(value)) return opts;
  return [...opts, value];
}

/**
 * select カラム（単一選択 / multiple 複数選択）のレンダリング。
 * EditableRecordTable の renderCellEditor と同じロジック。
 * multiple の場合は文字列値をカンマ区切りで配列化し、変更時は選択肢を結合して保存する。
 */
export function renderScheduleSelectField(row, column, updateField, setEditingRow) {
  // getOptionsがあれば現在のrow値に基づいた動的オプションを使用
  const opts = column.getOptions ? column.getOptions(row) : (column.options || ['']);
  const isMultiple = Boolean(column.multiple);
  const currentValue = isMultiple
    ? (Array.isArray(row[column.key])
      ? row[column.key]
      : String(row[column.key] || '').split(',').map((v) => v.trim()).filter(Boolean))
    : (row[column.key] || '');

  return (
    <select
      className="db-select-control"
      multiple={isMultiple}
      onChange={(e) => {
        if (isMultiple) {
          const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
          updateField(column.key, selected.join(', '));
          return;
        }
        const value = e.target.value;
        // OEM×対地 も座標の一部。成果物は自分の OEM×対地 を持つため、
        // 変更後も紐付けたままだと成果物管理では前の OEM×対地 の行として並び、
        // 変更後の OEM×対地 で絞ると見つけられなくなる。
        if (column.key === 'oem_region') {
          setEditingRow((prev) => prev ? {
            ...prev, oem_region: value, deliverables: [],
          } : prev);
          return;
        }
        // カスケードリセット: 大項目変更→中項目・小項目クリア
        // 成果物は座標ごとに紐づくので、座標が変わったらいったん外す。
        // 新しい座標で設定し直せばそこに登録され、何も設定しなければ解除される。
        // （進捗入力タブの詳細ポップアップと同じ挙動）
        if (column.key === 'large_category_name') {
          setEditingRow((prev) => prev ? {
            ...prev,
            large_category_name: value,
            middle_category_name: '',
            small_category: '',
            deliverables: [],
          } : prev);
          return;
        }
        // カスケードリセット: 中項目変更→小項目クリア
        if (column.key === 'middle_category_name') {
          setEditingRow((prev) => prev ? {
            ...prev,
            middle_category_name: value,
            small_category: '',
            deliverables: [],
          } : prev);
          return;
        }
        // 小項目は下位項目を持たないが、成果物の紐づき先は変わる
        if (column.key === 'small_category') {
          setEditingRow((prev) => prev ? {
            ...prev, small_category: value, deliverables: [],
          } : prev);
          return;
        }
        updateField(column.key, value);
      }}
      value={currentValue}
    >
      {opts.map((option) => (
        <option key={option || 'empty'} value={option}>
          {option || '選択してください'}
        </option>
      ))}
    </select>
  );
}

/* ---------- コンポーネント ---------- */

export default function ScheduleRecordEditor({ open, initialRow, onClose, onSaved, onDeleted, readOnly: readOnlyProp }) {
  const readOnly = readOnlyProp !== undefined ? readOnlyProp : !canEdit('ダッシュボード');
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [master, setMaster] = useState(null);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterError, setMasterError] = useState('');

  /* マスタデータを初回オープン時にロード（キャッシュ）
   * NOTE: masterLoading を deps に含めない。
   * React StrictMode ではエフェクトが二重実行されるため、
   * masterLoading を guard/deps に含めると2回目がスキップされ、
   * 1回目の active=false により永久にロードされなくなる。
   */
  useEffect(() => {
    if (!open || master) return;
    let active = true;
    setMasterLoading(true);
    setMasterError('');

    Promise.all([
      getOems(),
      getRegions(),
      getOemRegions(),
      getCategories(),
      getMiddleCategories(),
      getSmallCategories(),
      getActors(),
      getStatuses(),
      getPhases(),
      getEmployees(),
      getRisks(),
      getProblems(),
      getProgressSchedules(),
      getDeliverableManagements(),
    ])
      .then(([
        oems, regions, oemRegions, categories,
        middleCategories, smallCategories, actors,
        statuses, phases, employees, risks, problems, schedules,
        deliverableData,
      ]) => {
        if (!active) return;

        // 成果物候補は座標（OEM×対地＋カテゴリ〜Phase）単位。
        // 別の小項目で登録済みの成果物は選べないようにする。
        const deliverableOptionsByKey = buildDeliverableOptionsByCoordinate(
          deliverableData, schedules,
        );

        const oemByName = buildLookupMap(oems, 'oem');
        const regionByName = buildLookupMap(regions, 'region');
        const categoryByName = buildLookupMap(categories, 'category');
        const actorByName = buildLookupMap(actors, 'actor');
        const statusByName = buildLookupMap(statuses, 'status');
        const phaseByName = buildLookupMap(phases || [], 'phase');
        const middleLookup = buildMiddleCategoryLookup(middleCategories);
        const smallLookup = buildSmallCategoryLookup(smallCategories);
        const employeeByName = buildLookupMap(employees, 'user_name', 'employee_id');

        const oemRegionOptions = [''];
        const oemRegionLookup = new Map();
        oemRegions.forEach((item) => {
          const oemName = item.oem?.name || '';
          const regionName = item.region?.name || '';
          if (oemName && regionName) {
            const label = `${oemName}×${regionName}`;
            if (!oemRegionLookup.has(label)) {
              oemRegionOptions.push(label);
              oemRegionLookup.set(label, {
                porId: item.id,
                oemId: item.oem.id,
                regionId: item.region.id,
              });
            }
          }
        });

        const selectColumnKeys = BASE_COLUMNS
          .filter((c) => c.type === 'select')
          .map((c) => c.key);
        const selectOptions = buildSelectOptionsFromRows(schedules, selectColumnKeys);

        const employeeOptions = ['', ...employees.map((e) => e.user_name).filter(Boolean)];
        // Phase は既存レコードの使用値ではなくマスタ全件を候補にする
        const phaseOptions = ['', ...(phases || []).map((p) => p.phase).filter(Boolean)];

        // 後続タスク: OEM×対地別に選択肢を構築 + ID ルックアップ
        const successorTaskOptionsByOemRegion = {};
        const scheduleByOemSmall = new Map();
        schedules.forEach((r) => {
          const key = r.oem_region || '';
          if (!successorTaskOptionsByOemRegion[key]) successorTaskOptionsByOemRegion[key] = new Set();
          if (r.small_category) {
            successorTaskOptionsByOemRegion[key].add(r.small_category);
            if (r.id && r.oem_region) {
              const lookupKey = `${r.oem_region}::${r.small_category}`;
              if (!scheduleByOemSmall.has(lookupKey)) {
                scheduleByOemSmall.set(lookupKey, r.id);
              }
            }
          }
        });
        Object.keys(successorTaskOptionsByOemRegion).forEach((key) => {
          successorTaskOptionsByOemRegion[key] = ['', ...Array.from(successorTaskOptionsByOemRegion[key])];
        });

        // 関連リスク・関連課題の候補は行のOEM×対地に限定する（別OEM×対地への誤リンク防止）
        const relatedRiskByOemRegion = buildOptionsByOemRegion(risks, [
          (i) => i.title, (i) => i.noId, (i) => i.name, (i) => i.id,
        ]);
        const relatedProblemByOemRegion = buildOptionsByOemRegion(problems, [
          (i) => i.summary, (i) => i.title, (i) => i.noId, (i) => i.name, (i) => i.id,
        ]);
        // 既存スケジュール行に入力済みの値（カンマ結合）も同一OEM×対地の候補へ含める
        schedules.forEach((r) => {
          const oemKey = r.oem_region || '';
          if (!oemKey) return;
          const addTo = (grouped, raw) => {
            if (!grouped[oemKey]) grouped[oemKey] = [''];
            String(raw || '').split(',').map((v) => v.trim()).filter(Boolean)
              .forEach((v) => { if (!grouped[oemKey].includes(v)) grouped[oemKey].push(v); });
          };
          addTo(relatedRiskByOemRegion, r.related_risk);
          addTo(relatedProblemByOemRegion, r.related_problem);
        });

        // 選択肢の表示名と同じ優先順位のキーで、保存用の 名称 → ID ルックアップを構築
        const riskByTitle = new Map();
        risks.forEach((r) => {
          const key = r.title || r.noId || r.name || r.id;
          if (key != null && !riskByTitle.has(String(key))) riskByTitle.set(String(key), r.id);
        });
        const problemByTitle = new Map();
        problems.forEach((p) => {
          const key = p.summary || p.title || p.noId || p.name || p.id;
          if (key != null && !problemByTitle.has(String(key))) problemByTitle.set(String(key), p.id);
        });

        // カスケード選択用マップ: 大項目→中項目、中項目→小項目
        const middleCategoryOptionsByLarge = {};
        middleCategories.forEach((item) => {
          const largeName = item.large_category?.name;
          const middleName = item.middle_category;
          if (largeName && middleName) {
            if (!middleCategoryOptionsByLarge[largeName]) {
              middleCategoryOptionsByLarge[largeName] = new Set();
            }
            middleCategoryOptionsByLarge[largeName].add(middleName);
          }
        });
        Object.keys(middleCategoryOptionsByLarge).forEach((key) => {
          middleCategoryOptionsByLarge[key] = ['', ...Array.from(middleCategoryOptionsByLarge[key]).sort()];
        });

        const smallCategoryOptionsByMiddle = {};
        smallCategories.forEach((item) => {
          const middleName = item.middle_category?.name;
          const smallName = item.small_category;
          if (middleName && smallName) {
            if (!smallCategoryOptionsByMiddle[middleName]) {
              smallCategoryOptionsByMiddle[middleName] = new Set();
            }
            smallCategoryOptionsByMiddle[middleName].add(smallName);
          }
        });
        Object.keys(smallCategoryOptionsByMiddle).forEach((key) => {
          smallCategoryOptionsByMiddle[key] = ['', ...Array.from(smallCategoryOptionsByMiddle[key]).sort()];
        });

        setMaster({
          refs: {
            oemByName,
            regionByName,
            oemRegionLookup,
            categoryByName,
            actorByName,
            statusByName,
            phaseByName,
            middleByPair: middleLookup.pairMap,
            middleByName: middleLookup.byNameMap,
            smallByPair: smallLookup.pairMap,
            smallByName: smallLookup.byNameMap,
            employeeByName,
            scheduleByOemSmall,
            riskByTitle,
            problemByTitle,
          },
          options: {
            oemRegion: oemRegionOptions,
            employee: employeeOptions,
            phase: phaseOptions,
            successorTaskOptionsByOemRegion,
            relatedRiskByOemRegion,
            relatedProblemByOemRegion,
            selectByKey: selectOptions,
            middleCategoryOptionsByLarge,
            smallCategoryOptionsByMiddle,
            deliverableOptionsByKey,
          },
        });
      })
      .catch((err) => {
        if (!active) return;
        console.error('[ScheduleRecordEditor] Master data load failed:', err);
        setMasterError('マスタデータの取得に失敗しました。再度開き直してください。');
      })
      .finally(() => {
        if (active) setMasterLoading(false);
      });

    return () => { active = false; };
  }, [open, master]);

  /* 親から渡された行を編集用にコピー */
  useEffect(() => {
    if (initialRow) {
      setEditingRow({
        ...initialRow,
        milestone: Boolean(initialRow.milestone),
      });
    } else {
      setEditingRow(null);
    }
  }, [initialRow]);

  /**
   * マスタデータを使ってカラムにオプションを付与。
   * initialRow の現在値がオプションに無い場合は自動追加する。
   */
  const columns = useMemo(() => {
    const row = initialRow;
    if (!master) {
      // マスタ未ロード時: row値からオプションを仮生成
      return BASE_COLUMNS.map((col) => {
        if (col.type !== 'select') return col;
        const value = row?.[col.key];
        const opts = value ? ['', value] : [''];
        return { ...col, options: opts };
      });
    }
    const { options } = master;
    return BASE_COLUMNS.map((col) => {
      let colOptions;
      if (col.key === 'oem_region') colOptions = options.oemRegion;
      else if (col.key === 'assignees_display') colOptions = options.employee;
      else if (col.key === 'phase_name') colOptions = options.phase;
      else if (col.key === 'successor_task') {
        const oemKey = row?.oem_region || '';
        colOptions = options.successorTaskOptionsByOemRegion[oemKey] || [''];
      }
      else if (col.key === 'related_risk') {
        colOptions = options.relatedRiskByOemRegion[row?.oem_region || ''] || [''];
      }
      else if (col.key === 'related_problem') {
        colOptions = options.relatedProblemByOemRegion[row?.oem_region || ''] || [''];
      }
      else if (col.type === 'select') colOptions = /* istanbul ignore next -- buildSelectOptionsFromRows が select 列のキーを必ず作る */ (options.selectByKey[col.key] || ['']);
      else return col;

      // 現在のrow値がオプションに含まれていなければ追加
      colOptions = ensureValueInOptions(colOptions, row?.[col.key]);

      // カスケード選択: 大項目→中項目→小項目
      if (col.key === 'middle_category_name') {
        const allOpts = colOptions;
        return {
          ...col,
          options: allOpts,
          getOptions: (currentRow) => {
            const large = currentRow?.large_category_name;
            if (large && options.middleCategoryOptionsByLarge[large]) {
              return ensureValueInOptions(options.middleCategoryOptionsByLarge[large], currentRow.middle_category_name);
            }
            return allOpts;
          },
        };
      }
      if (col.key === 'small_category') {
        const allOpts = colOptions;
        return {
          ...col,
          options: allOpts,
          getOptions: (currentRow) => {
            const middle = currentRow?.middle_category_name;
            if (middle && options.smallCategoryOptionsByMiddle[middle]) {
              return ensureValueInOptions(options.smallCategoryOptionsByMiddle[middle], currentRow.small_category);
            }
            return allOpts;
          },
        };
      }

      return { ...col, options: colOptions };
    });
  }, [master, initialRow]);

  const updateField = useCallback((key, value) => {
    /* istanbul ignore next -- 編集中のみ呼ばれるため prev は常に存在する */
    setEditingRow((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  /**
   * EditableRecordTable の renderCellEditor と同じレンダリングロジック。
   */
  const renderField = useCallback((row, column) => {
    if (column.key === 'deliverables') {
      const opts = getDeliverableOptionsForSchedule(
        master?.options?.deliverableOptionsByKey, row,
      );
      return (
        <DeliverableSetEditor
          value={Array.isArray(row.deliverables) ? row.deliverables : []}
          onChange={(next) => updateField('deliverables', next)}
          options={opts}
        />
      );
    }

    if (column.type === 'multi_items') {
      const raw = row[column.key] || '';
      const filled = raw.split(',').map((s) => s.trim()).filter(Boolean);
      const hasNewSlot = raw.trimEnd().endsWith(',');
      const items = hasNewSlot ? [...filled, ''] : filled;

      let opts = [''];
      if (master?.options) {
        /* istanbul ignore if -- 成果物は custom 列に置き換わっており multi_items の 'deliverable' 列は存在しない */
        if (column.key === 'deliverable') opts = master.options.deliverable || [''];
        else if (column.key === 'assignees_display') opts = /* istanbul ignore next -- 担当者候補は必ず構築される */ (master.options.employee || ['']);
        else if (column.key === 'related_risk') {
          // 関連リスク候補は行のOEM×対地に限定（successor_task と同方式）
          const oemKey = row.oem_region || '';
          opts = master.options.relatedRiskByOemRegion[oemKey] || [''];
        }
        else if (column.key === 'related_problem') {
          // 関連課題候補は行のOEM×対地に限定（successor_task と同方式）
          const oemKey = row.oem_region || '';
          opts = master.options.relatedProblemByOemRegion[oemKey] || [''];
        }
        else /* istanbul ignore else -- multi_items 列はこの4種のみ */ if (column.key === 'successor_task') {
          const oemKey = row.oem_region || '';
          opts = master.options.successorTaskOptionsByOemRegion[oemKey] || [''];
        }
      }

      const addItem = () => {
        const current = row[column.key] || '';
        const trimmed = current.replace(/,\s*$/, '');
        updateField(column.key, trimmed ? trimmed + ',' : ',');
      };

      if (items.length === 0) {
        return (
          <button
            type="button"
            onClick={addItem}
            style={{
              border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
              padding: '4px 12px', fontSize: '13px', color: '#6b7280', cursor: 'pointer',
            }}
          >
            ＋ 追加
          </button>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {items.map((item, i) => {
            const selectOpts = opts.includes(item) ? opts : [...opts, item];
            return (
              <div key={`${column.key}-${i}`} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <select
                  className="db-select-control"
                  value={item}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[i] = e.target.value;
                    updateField(column.key, newItems.filter(Boolean).join(', '));
                  }}
                  style={{ flex: 1 }}
                >
                  {selectOpts.map((opt) => (
                    <option key={opt || 'empty'} value={opt}>
                      {opt || '選択してください'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const newItems = items.filter((_, idx) => idx !== i);
                    updateField(column.key, newItems.join(', '));
                  }}
                  style={{
                    border: 'none', background: 'transparent', color: '#ef4444',
                    fontSize: '16px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
                  }}
                  title="削除"
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addItem}
            style={{
              border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
              padding: '3px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            ＋ 追加
          </button>
        </div>
      );
    }

    if (column.type === 'select') {
      return renderScheduleSelectField(row, column, updateField, setEditingRow);
    }

    if (column.type === 'checkbox') {
      return (
        <input
          checked={Boolean(row[column.key])}
          onChange={(e) => updateField(column.key, e.target.checked)}
          type="checkbox"
        />
      );
    }

    if (['comment', 'pmoComment', 'memo'].includes(column.key)) {
      return (
        <LinkableTextArea
          onChange={(next) => updateField(column.key, next)}
          value={row[column.key]}
        />
      );
    }

    /* istanbul ignore else -- 編集できる text 列は備考のみで、上の LinkableTextArea が受け持つ */
    if (column.type === 'date') {
      return (
        <input
          className="db-input-control"
          max="9999-12-31"
          onChange={(e) => updateField(column.key, e.target.value)}
          type="date"
          value={row[column.key] || ''}
        />
      );
    }

    /* istanbul ignore next -- 将来 text 列を編集可能にしたときのフォールバック */
    return (
      <input
        className="db-input-control"
        onChange={(e) => updateField(column.key, e.target.value)}
        type="text"
        value={row[column.key] || ''}
      />
    );
  }, [master, updateField]);

  const formatValue = useCallback((column, value) => {
    if (column.key === 'deliverables') {
      return (
        <DeliverableSetEditor
          value={Array.isArray(value) ? value : []}
          onChange={/* istanbul ignore next -- readOnly では DeliverableSetEditor が先に return するため呼ばれない */ () => {}}
          readOnly
        />
      );
    }
    if (column.type === 'checkbox') return value ? 'はい' : 'いいえ';
    return value || '—';
  }, []);

  const handleDelete = useCallback(async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row?.id) return;
    setDeleting(true);
    setSaveError('');
    try {
      await deleteProgressSchedule(row.id, row.updated_at_iso ?? null);
      setEditingRow(null);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      console.error('[ScheduleRecordEditor] Delete error:', err);
      setSaveError(err.message || '削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  }, [onDeleted, onClose]);

  const handleSave = useCallback(async () => {
    if (!editingRow?.id) return;

    const missingFields = columns
      .filter((c) => c.required && !c.readOnly)
      .filter((c) => {
        const val = editingRow[c.key];
        /* istanbul ignore if -- 必須列はすべて文字列項目のため配列にならない */
        if (Array.isArray(val)) return val.length === 0;
        /* istanbul ignore if -- 必須列にチェックボックスは無い */
        if (typeof val === 'boolean') return false;
        return !val && val !== 0;
      })
      .map((c) => c.label);
    if (missingFields.length > 0) {
      setSaveError(`以下の必須項目が未入力です：\n${missingFields.join('、')}`);
      return;
    }

    // 成果物セット: 成果物名⇄URLの相互必須（成果物1件ごと）
    const deliverableError = validateDeliverables(editingRow.deliverables);
    if (deliverableError) {
      setSaveError(deliverableError);
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      /* istanbul ignore next -- マスタ未ロード中は保存ボタンが無効なのでここへ来ない */
      const refs = master?.refs || {};
      const payload = toPatchPayload(editingRow, refs);
      await updateProgressSchedule(editingRow.id, payload);
      setEditingRow(null);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('[ScheduleRecordEditor] PATCH error:', err);
      if (err.status === 409) {
        setSaveError(buildConflictMessage(err.message));
      } else {
        setSaveError(err.message || '保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  }, [editingRow, master, columns, onSaved, onClose]);

  const handleClose = useCallback(() => {
    setSaveError('');
    onClose?.();
  }, [onClose]);

  const popupError = saveError || masterError;

  return (
    <>
      <RecordDetailPopup
        columns={columns}
        deleting={deleting}
        formatValue={formatValue}
        onClose={handleClose}
        onDelete={readOnly ? undefined : handleDelete}
        onSave={readOnly ? undefined : handleSave}
        open={open && Boolean(editingRow)}
        readOnly={readOnly}
        renderField={renderField}
        row={editingRow}
        saving={saving}
        savingDisabled={masterLoading || !master}
        title="進捗管理レコード詳細"
      />
      {popupError && (
        <div className="db-error-modal-overlay" onClick={() => { setSaveError(''); setMasterError(''); }} role="presentation">
          <div className="db-error-modal" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <div className="db-error-modal-icon">!</div>
            <p className="db-error-modal-message">{popupError}</p>
            <button className="db-error-modal-button" onClick={() => { setSaveError(''); setMasterError(''); }} type="button">
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
